'use strict';

const { CSRF_TOKEN_ENDPOINT } = require('public/js/lib/csrf/token-fetcher');
const { SUPPLIER_RESOLUTION_ENDPOINT, waitForSupplierResolution } = require('public/js/lib/supplier-ready');
const utils = require('public/js/helpers/utils');

// Deferring these would deadlock: each one is what settles a gate the deferral waits on.
const GATE_SETTLING_ENDPOINTS = [CSRF_TOKEN_ENDPOINT, SUPPLIER_RESOLUTION_ENDPOINT];

/**
 * CSRF config is emitted by the backend into `rp_app.data.csrf_config`.
 * We keep a minimal fallback so non-browser contexts (tests) don't crash.
 *
 * @returns {{field_name: string, header_name: string, input_id: string, meta_name: string}}
 */
function getCsrfConfig() {
	const fallback = {
		field_name: 'csrf_token',
		header_name: 'X-CSRF-TOKEN',
		input_id: '__csrf_value_field__',
		meta_name: 'csrf-token',
	};

	const cfg = globalThis?.rp_app?.data?.csrf_config;
	if (!cfg || typeof cfg !== 'object') {
		return fallback;
	}

	return {
		field_name: typeof cfg.field_name === 'string' && cfg.field_name ? cfg.field_name : fallback.field_name,
		header_name: typeof cfg.header_name === 'string' && cfg.header_name ? cfg.header_name : fallback.header_name,
		input_id: typeof cfg.input_id === 'string' && cfg.input_id ? cfg.input_id : fallback.input_id,
		meta_name: typeof cfg.meta_name === 'string' && cfg.meta_name ? cfg.meta_name : fallback.meta_name,
	};
}

/**
 * Gets the CSRF token from the meta tag.
 * @param {Window|Document|undefined} context
 * @returns {string}
 */
function getCsrfTokenFromMeta(context) {
	const doc = context?.document || context || globalThis.document;
	if (!doc || typeof doc.querySelector !== 'function') {
		return '';
	}

	const csrfConfig = getCsrfConfig();
	const meta = doc.querySelector(`meta[name="${csrfConfig.meta_name}"]`);
	if (!meta || typeof meta.getAttribute !== 'function') {
		return '';
	}

	return meta.getAttribute('content') || '';
}

/**
 * Normalizes the headers object.
 * @param {object} headers - The headers object to normalize.
 * @returns {object}
 */
function normalizeHeaders(headers) {
	if (!headers) {
		return {};
	}

	if (typeof headers.forEach === 'function') {
		const obj = {};
		headers.forEach((value, key) => {
			obj[key] = value;
		});
		return obj;
	}

	if (typeof headers === 'object') {
		return { ...headers };
	}

	return {};
}

/**
 * Checks if a header exists in the headers object.
 * @param {object} headersObj - The headers object to check.
 * @param {string} headerNameLower
 * @returns {boolean}
 */
function hasHeader(headersObj, headerNameLower) {
	return Object.keys(headersObj).some((key) => key.toLowerCase() === headerNameLower);
}

/**
 * Adds the CSRF token to the headers object.
 * @param {object} options - The options object to add the CSRF token to.
 * @param {string} token - The CSRF token to add to the headers object.
 * @returns {object}
 */
function withCsrfHeaderFromConfig(options, token) {
	const opts = options || {};
	const csrfConfig = getCsrfConfig();
	const headerName = String(csrfConfig.header_name || '');
	if (!headerName) {
		return opts;
	}

	const headerNameLower = headerName.toLowerCase();
	const headers = normalizeHeaders(opts.headers);
	if (hasHeader(headers, headerNameLower)) {
		return opts;
	}

	if (!token) {
		return opts;
	}

	headers[headerName] = token;
	return { ...opts, headers };
}

/**
 * Checks if a form action target is safe (same-origin) for CSRF token injection.
 * @param {string} action
 * @returns {boolean}
 */
function isSameOriginFormAction(action) {
	if (!action) {
		return true;
	}

	if (['mailto:', 'tel:', 'javascript:'].some((prefix) => action.startsWith(prefix))) {
		return false;
	}

	if (['http://', 'https://', '//'].some((prefix) => action.startsWith(prefix))) {
		const aTag = document.createElement('a');
		aTag.href = action.startsWith('//') ? (location.protocol + action) : action;
		return aTag.protocol === location.protocol && aTag.host === location.host;
	}

	return true;
}

/**
 * Checks if a method is a mutating method.
 * @param {string} method
 * @returns {boolean}
 */
function isMutatingMethod(method) {
	const upper = String(method || '').toUpperCase();
	return ['POST', 'PUT', 'DELETE', 'PATCH'].includes(upper);
}

/**
 * Checks if a request is a mutating request.
 * @param {unknown} optionsOrMethod
 * @returns {boolean}
 */
function isMutatingRequest(optionsOrMethod) {
	if (typeof optionsOrMethod === 'string') {
		return isMutatingMethod(optionsOrMethod);
	}

	if (!optionsOrMethod || typeof optionsOrMethod !== 'object') {
		return false;
	}

	const method = optionsOrMethod.method || optionsOrMethod.type || 'GET';
	return isMutatingMethod(String(method));
}

/**
 * Returns true when the given request options represent a same-origin
 * mutating request that should include a CSRF header.
 * @param {unknown} options
 * @returns {boolean}
 */
function shouldAttachCsrfHeader(options) {
	if (!isMutatingRequest(options)) {
		return false;
	}

	if (!options || typeof options !== 'object') {
		return true;
	}

	if (options.crossDomain === true) {
		return false;
	}

	const url = utils.getUrlFromRequest(options);
	return isSameOriginFormAction(url);
}

/**
 * Returns true when the request should be deferred until the page-load
 * gates settle. We defer all same-origin requests so the server doesn't
 * allocate competing sessions before the token GET lands, and so the burst
 * cannot overwrite the fulfillment hash that supplier resolution commits.
 * The requests that settle those gates are exempt to avoid deadlock.
 * @param {unknown} options
 * @returns {boolean}
 */
function shouldDeferRequest(options) {
	if (options && typeof options === 'object' && options.crossDomain === true) {
		return false;
	}

	const url = utils.getUrlFromRequest(options);
	if (url && !isSameOriginFormAction(url)) {
		return false;
	}

	return !GATE_SETTLING_ENDPOINTS.includes(utils.getPathnameFromUrl(url));
}

/**
 * Get CSRF token value from the hidden input.
 * @returns {string}
 */
const getToken = () => {
	const csrfConfig = getCsrfConfig();
	const valueField = document.getElementById(csrfConfig.input_id);

	if (!valueField) {
		return '';
	}

	const valueValue = valueField.getAttribute('value');
	return valueValue || '';
};

/**
 * Resolves once the CSRF bootstrap (`bootstrap.js`) has settled — i.e.
 * `globalThis.__rp_csrf_ready_promise` is fulfilled and the meta tag /
 * hidden input have a token. No-op when the bootstrap promise hasn't
 * been installed (e.g. test environments, older pages).
 *
 * @returns {Promise<void>}
 */
function waitForCsrf() {
	const csrfReady = globalThis.__rp_csrf_ready_promise;
	if (csrfReady && typeof csrfReady.then === 'function') {
		return csrfReady.then(function () {});
	}
	return Promise.resolve();
}

/**
 * `fetch` wrapper that defers until the page-load gates settle, then
 * attaches the CSRF header per `rp_app.data.csrf_config`. Mirrors what
 * `lib/request.js` does for jQuery `$ajax` callers — keeps fetch-based
 * modules from re-implementing the meta-read + header-attach dance.
 *
 * Waits on supplier resolution as well as the token, because React islands mount during the
 * page-load burst and reach the network through here rather than through the deferral wrapper.
 * Gating only `$ajax` would leave those islands racing the fulfillment hash commit. Callers
 * running after page load pay nothing: both gates are already settled by then.
 *
 * @param {string} url
 * @param {RequestInit} init
 * @returns {Promise<Response>}
 */
function fetchWithCsrf(url, init) {
	return Promise.all([waitForCsrf(), waitForSupplierResolution()]).then(function () {
		const token = getCsrfTokenFromMeta() || getToken();
		return fetch(url, withCsrfHeaderFromConfig(init, token));
	});
}

module.exports = {
	fetchWithCsrf,
	getCsrfConfig,
	getCsrfTokenFromMeta,
	getToken,
	hasHeader,
	isMutatingMethod,
	isMutatingRequest,
	isSameOriginFormAction,
	normalizeHeaders,
	shouldAttachCsrfHeader,
	shouldDeferRequest,
	waitForCsrf,
	withCsrfHeaderFromConfig,
};
